import { Copy, Edit, MoreHorizontal, Star, Trash2 } from 'lucide-react';
import React, { useEffect, useState } from 'react';

import { Box, Button, Input, Menu, MenuButton, MenuDivider, MenuItem, MenuList, Portal, Tab, TabList, Tabs } from '@chakra-ui/react';

import { DesignPagingProps } from './design-paging.type';
import { Designs } from './designs.type';
import { lang } from '@lang';
import { RtaIconButton } from '@components';

import { useDeleteCustomerBlueprintFeature } from '@features/customer/use-delete-customer-blueprint-feature';

type DesignTabSelectionProps = DesignPagingProps & {
	designs: Designs,
	selected?: string | null,
	disabled?: boolean,
	favoriteId?: string | null,
	onSelect: (id: string, version: number | null, baseVersion: number | null) => void,
	onDesignNameChange?: (id: string, name: string) => void
	onDelete?: (id: string) => void
	onDuplicate?: (id: string) => void
	onFavorite?: (id: string) => void
};

// Extract tab content into a separate component to ensure hooks are called unconditionally
type DesignTabContentProps = {
	design: Designs[number];
	isSelected: boolean;
	isEditing: boolean;
	isFavorite: boolean;
	editedName: string;
	onEditClick: () => void;
	onSaveClick: () => void;
	onDuplicateClick: () => void;
	onFavoriteClick: (designId: string) => void;
	onSpecialKey: (e: React.KeyboardEvent<HTMLInputElement>) => void;
	onNameChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
};

const DesignTabContent = (props: DesignTabContentProps): React.ReactElement => {
	const {
		design, isSelected, isEditing, isFavorite, editedName,
		onEditClick, onSaveClick, onDuplicateClick, onFavoriteClick, onSpecialKey, onNameChange
	} = props;

	// Call hook unconditionally at the top level
	const deleteFeature = useDeleteCustomerBlueprintFeature(design.id, design.submissionId);

	if (isEditing) {
		return (
			<Input
				value={editedName}
				placeholder={design.description}
				size="xs"
				autoFocus
				onKeyDown={onSpecialKey}
				onChange={onNameChange}
				onBlur={onSaveClick}
			/>
		);
	}

	return (
		<>
			<Box
				as="span"
				cursor="pointer"
				onClick={(e: React.MouseEvent) => { e.stopPropagation(); onFavoriteClick(design.id); }}
				display="inline-flex"
				alignItems="center"
				color={isFavorite ? 'blue.400' : 'gray.300'}
				_hover={{ color: isFavorite ? 'blue.500' : 'gray.500' }}
			>
				<Star size={12} fill={isFavorite ? 'currentColor' : 'none'} />
			</Box>
			<>{(design.description ?? '') === '' ? 'no description' : design.description} { design.isDirty ? '*' : '' }</>
			{ isSelected && (
				<Menu>
					<MenuButton
						as={RtaIconButton}
						icon={<MoreHorizontal />}
						height="15px"
						label="Design Options"
						onClick={(e: React.MouseEvent) => e.stopPropagation()}
						display="inline-flex"
						alignItems="center"
					/>
					<Portal>
						<MenuList zIndex="popover" fontSize="sm">
							<MenuItem
								icon={<Edit size={14} />}
								isDisabled={design.readOnly}
								onClick={(e) => { e.stopPropagation(); onEditClick(); }}
							>
								Edit Name
							</MenuItem>
							<MenuItem
								icon={<Copy size={14} />}
								onClick={(e) => { e.stopPropagation(); onDuplicateClick(); }}
							>
								Duplicate
							</MenuItem>
							<MenuDivider />
							<MenuItem
								icon={<Trash2 size={14} />}
								isDisabled={deleteFeature.disabled}
								onClick={(e) => { e.stopPropagation(); deleteFeature.handler(); }}
								color="red.500"
							>
								Delete
							</MenuItem>
						</MenuList>
					</Portal>
				</Menu>
			) }
		</>
	);
};

// eslint-disable-next-line max-lines-per-function
export const DesignTabSelection = (props: DesignTabSelectionProps): React.ReactElement => {
	const { designs, disabled, selected = null, favoriteId = null, remaining = 0, isLoadingMore = false, onSelect, onLoadMore } = props;
	const [isEditing, setIsEditing] = useState(false);
	const [editedName, setEditedName] = useState('');

	useEffect(() => {
		setIsEditing(false);
		setEditedName('');
	}, [selected]);

	// eslint-disable-next-line no-undefined
	let tabIndex = selected ? designs.findIndex((design) => design.id === selected) : 0;
	if (tabIndex < 0) tabIndex = 0;
	const idsKey = designs.map((d) => d.id).join('|');

	const handleEditClick = () => {
		setIsEditing(true);
		setEditedName('');
	};

	const handleSaveClick = () => {
		setIsEditing(false);
		const designId = designs.find((design) => design.id === selected)?.id;
		if (designId && editedName !== '') {
			props.onDesignNameChange?.(designId, editedName);
		}
	};

	// const handleDeleteClick = () => {
	// 	const designId = designs.find((design) => design.id === selected)?.id;
	// 	if (designId) {
	// 		console.log(`Deleting design with id: ${designId}`);
	// 		props.onDelete?.(designId);
	// 	}
	// };

	const handleDuplicateClick = () => {
		const designId = designs.find((design) => design.id === selected)?.id;
		if (designId) {
			console.log(`Duplicating design with id: ${designId}`);
			props.onDuplicate?.(designId);
		}
	};

	const handleFavoriteClick = (designId: string) => {
		props.onFavorite?.(designId);
	};

	const handleSpecialKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
		switch (e.key) {
			case ' ':
				e.preventDefault();
				setEditedName(`${editedName} `);
				break;
			case 'Escape':
				setIsEditing(false);
				break;
			case 'Enter':
				handleSaveClick();
				break;
			default:
				break;
		}
	};

	const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setEditedName(e.target.value.trim());
	};

	return <Box width="2000px">
		<Tabs variant="enclosed-colored" size="sm"
			key={`${selected ?? 'none'}:${idsKey}`}
			index={tabIndex}
			onChange={(i) => onSelect(designs[i].id, designs[i].version, designs[i].baseVersion)}
		>
			<TabList>
				{designs.map((design) => (
					<Tab
						isDisabled={disabled}
						key={`${design.id}:${design.version}`}
						minWidth="10em"
						fontSize="sm"
						display="flex"
						alignItems="center"
						gap="5px"
						bg={undefined}
						style={{
							whiteSpace: 'nowrap',
							overflow: 'hidden',
							textOverflow: 'ellipsis',
							textAlign: 'left'
						}}
					>
						<DesignTabContent
							design={design}
							isSelected={selected === design.id}
							isEditing={isEditing && selected === design.id}
							isFavorite={design.id === favoriteId}
							editedName={editedName}
							onEditClick={handleEditClick}
							onSaveClick={handleSaveClick}
							onDuplicateClick={handleDuplicateClick}
							onFavoriteClick={handleFavoriteClick}
							onSpecialKey={handleSpecialKey}
							onNameChange={handleNameChange}
						/>
					</Tab>
				))}
				{remaining > 0 && <Button
					alignSelf="center"
					size="xs"
					ml="8px"
					flexShrink={0}
					isLoading={isLoadingMore}
					title={lang.customer.tooltip.loadMoreDesigns}
					onClick={() => onLoadMore?.()}>
					{`${lang.customer.label.loadMoreDesigns} (${remaining})`}
				</Button>}
			</TabList>
		</Tabs>
	</Box>;
};
